/**
 * Host Validation Utility
 * 
 * Validates that the main application is only accessed from allowed hosts
 * (localhost and app.sayify.pro) while allowing public intake pages to work
 * on any subdomain or custom domain.
 */

/**
 * Check if the current route is a public intake page
 * @param {string} pathname - The current pathname (e.g., window.location.pathname)
 * @returns {boolean} - True if this is a public intake route
 */
export const isPublicIntakeRoute = (pathname) => {
    // Match /i/:slug pattern
    return /^\/i\/[^/]+/.test(pathname);
};

/**
 * Check if the hostname is an allowed host for the main application
 * @param {string} hostname - The hostname to check (e.g., window.location.hostname)
 * @returns {boolean} - True if this is an allowed host
 */
export const isAllowedHost = (hostname) => {
    // Allow localhost (with or without port)
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return true;
    }

    // Allow app.sayify.pro
    if (hostname === 'app.sayify.pro') {
        return true;
    }

    return false;
};

/**
 * Validate the current host based on route context
 * @returns {Object} - { isValid: boolean, shouldShowError: boolean, reason: string }
 */
export const validateHost = () => {
    const hostname = window.location.hostname;
    const pathname = window.location.pathname;

    // Public intake pages are allowed on any domain
    if (isPublicIntakeRoute(pathname)) {
        return {
            isValid: true,
            shouldShowError: false,
            reason: 'Public intake route - all domains allowed',
        };
    }

    // All other routes must be on allowed hosts
    const hostAllowed = isAllowedHost(hostname);

    if (!hostAllowed) {
        return {
            isValid: false,
            shouldShowError: true,
            reason: `Invalid host: ${hostname}. This application can only be accessed from localhost or app.sayify.pro.`,
        };
    }

    return {
        isValid: true,
        shouldShowError: false,
        reason: 'Valid host',
    };
};

/**
 * Get user-friendly error message for invalid hosts
 * @param {string} hostname - The invalid hostname
 * @returns {string} - User-friendly error message
 */
export const getHostErrorMessage = (hostname) => {
    return `Access Denied: This application can only be accessed from authorized domains. Current host: ${hostname}`;
};
